import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Sparkles, Upload, AlertTriangle, CheckCircle, Loader2, FileText, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FOOD_CATEGORIES = [
  "Vegetables", "Fruit", "Grains & Bread", "Legumes", "Dairy",
  "Fish & Seafood", "Meat & Poultry", "Eggs", "Nuts & Seeds",
  "Snacks & Sweets", "Condiments & Sauces", "Beverages", "Ready Meals"
];

const RECIPE_CATEGORIES = [
  "Breakfast", "Lunch", "Dinner", "Snacks", "World Cuisine", "High-Protein", "Low-Sugar"
];

const RESCUE_TIERS = ["exercise", "diet", "lifestyle", "any"];

export default function AIGeneratePage() {
  const [activeTab, setActiveTab] = useState("foods");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  
  // Foods form
  const [foodsForm, setFoodsForm] = useState({
    prompt: "",
    category: "Vegetables",
    count: 5
  });
  
  // Recipes form
  const [recipesForm, setRecipesForm] = useState({
    prompt: "",
    category: "Any",
    count: 2
  });
  
  // Rescue form
  const [rescueForm, setRescueForm] = useState({
    tier: "any",
    count: 3
  });
  
  // Combos form
  const [combosForm, setCombosForm] = useState({
    theme: "",
    count: 2
  });
  
  // CSV import state
  const [csvFile, setCsvFile] = useState(null);
  const [csvValidation, setCsvValidation] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  
  const getToken = () => localStorage.getItem('carbiq_admin_token');
  const getAdminEmail = () => {
    try {
      const token = getToken();
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub || 'admin@carbiq.app';
      }
    } catch {}
    return 'admin@carbiq.app';
  };
  
  const generateFoods = async () => {
    if (!foodsForm.prompt.trim()) {
      toast.error('Please enter a description');
      return;
    }
    
    setLoading(true);
    setResults(null);
    
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/ai-generate/foods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          ...foodsForm,
          adminEmail: getAdminEmail()
        })
      });
      
      if (response.status === 429) {
        toast.error('Rate limit exceeded: max 3 AI generations per hour');
        return;
      }
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Generation failed');
      }
      
      const data = await response.json();
      setResults(data);
      toast.success(`Generated ${data.generated} foods - added to review queue`);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const generateRecipes = async () => {
    if (!recipesForm.prompt.trim()) {
      toast.error('Please enter a description');
      return;
    }
    
    setLoading(true);
    setResults(null);
    
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/ai-generate/recipes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          ...recipesForm,
          adminEmail: getAdminEmail()
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Generation failed');
      }
      
      const data = await response.json();
      setResults(data);
      toast.success(`Generated ${data.generated} recipes - added to review queue`);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const generateRescue = async () => {
    setLoading(true);
    setResults(null);
    
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/ai-generate/rescue-actions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          ...rescueForm,
          adminEmail: getAdminEmail()
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Generation failed');
      }
      
      const data = await response.json();
      setResults(data);
      toast.success(`Generated ${data.generated} rescue actions - added to review queue`);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const generateCombos = async () => {
    if (!combosForm.theme.trim()) {
      toast.error('Please enter a theme');
      return;
    }
    
    setLoading(true);
    setResults(null);
    
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/ai-generate/combos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          ...combosForm,
          adminEmail: getAdminEmail()
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Generation failed');
      }
      
      const data = await response.json();
      setResults(data);
      toast.success(`Generated ${data.generated} combos - added to review queue`);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setCsvFile(file);
    setCsvValidation(null);
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/v1/admin/import/foods/csv`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Validation failed');
      }
      
      const data = await response.json();
      setCsvValidation(data);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const confirmCsvImport = async () => {
    if (!csvValidation?.importSessionId) return;
    
    setCsvImporting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/import/foods/csv/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          importSessionId: csvValidation.importSessionId,
          adminEmail: getAdminEmail()
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Import failed');
      }
      
      const data = await response.json();
      toast.success(`${data.addedToQueue} foods added to review queue`);
      setCsvFile(null);
      setCsvValidation(null);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCsvImporting(false);
    }
  };
  
  const downloadTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/import/foods/csv/template`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'carbiq_foods_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      toast.error('Failed to download template');
    }
  };
  
  return (
    <div className="space-y-6" data-testid="ai-generate-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          AI Content Generator
        </h1>
        <p className="text-muted-foreground">Generate diabetic-friendly content using Claude AI</p>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="foods">Generate Foods</TabsTrigger>
          <TabsTrigger value="recipes">Generate Recipes</TabsTrigger>
          <TabsTrigger value="rescue">Rescue & Combos</TabsTrigger>
          <TabsTrigger value="csv">CSV Import</TabsTrigger>
        </TabsList>
        
        {/* Generate Foods Tab */}
        <TabsContent value="foods">
          <Card>
            <CardHeader>
              <CardTitle>Generate Foods</CardTitle>
              <CardDescription>Use AI to generate food items with nutritional data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Description</Label>
                <Textarea
                  placeholder="e.g., 5 Japanese fermented vegetables suitable for diabetics"
                  value={foodsForm.prompt}
                  onChange={(e) => setFoodsForm({ ...foodsForm, prompt: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select
                    value={foodsForm.category}
                    onValueChange={(v) => setFoodsForm({ ...foodsForm, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FOOD_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>How many? (1-10)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={foodsForm.count}
                    onChange={(e) => setFoodsForm({ ...foodsForm, count: parseInt(e.target.value) || 5 })}
                  />
                </div>
              </div>
              <Button onClick={generateFoods} disabled={loading} className="w-full">
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Claude is researching nutritional data...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate with AI
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Generate Recipes Tab */}
        <TabsContent value="recipes">
          <Card>
            <CardHeader>
              <CardTitle>Generate Recipes</CardTitle>
              <CardDescription>Create diabetic-friendly recipes with AI</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Description</Label>
                <Textarea
                  placeholder="e.g., High-protein Korean dinner recipe, diabetic-friendly"
                  value={recipesForm.prompt}
                  onChange={(e) => setRecipesForm({ ...recipesForm, prompt: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select
                    value={recipesForm.category}
                    onValueChange={(v) => setRecipesForm({ ...recipesForm, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Any">Any</SelectItem>
                      {RECIPE_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>How many? (1-5)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={5}
                    value={recipesForm.count}
                    onChange={(e) => setRecipesForm({ ...recipesForm, count: parseInt(e.target.value) || 2 })}
                  />
                </div>
              </div>
              <Button onClick={generateRecipes} disabled={loading} className="w-full">
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating recipes...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate with AI
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Rescue & Combos Tab */}
        <TabsContent value="rescue">
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Rescue Actions</CardTitle>
                <CardDescription>Generate glucose management actions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Tier</Label>
                  <Select
                    value={rescueForm.tier}
                    onValueChange={(v) => setRescueForm({ ...rescueForm, tier: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RESCUE_TIERS.map(tier => (
                        <SelectItem key={tier} value={tier}>
                          {tier.charAt(0).toUpperCase() + tier.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Count (1-5)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={5}
                    value={rescueForm.count}
                    onChange={(e) => setRescueForm({ ...rescueForm, count: parseInt(e.target.value) || 3 })}
                  />
                </div>
                <Button onClick={generateRescue} disabled={loading} className="w-full">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate
                </Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Smart Combos</CardTitle>
                <CardDescription>Generate food combinations</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Theme</Label>
                  <Textarea
                    placeholder="e.g., Mediterranean anti-inflammatory foods"
                    value={combosForm.theme}
                    onChange={(e) => setCombosForm({ ...combosForm, theme: e.target.value })}
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Count (1-3)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={3}
                    value={combosForm.count}
                    onChange={(e) => setCombosForm({ ...combosForm, count: parseInt(e.target.value) || 2 })}
                  />
                </div>
                <Button onClick={generateCombos} disabled={loading} className="w-full">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* CSV Import Tab */}
        <TabsContent value="csv">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                CSV Import
              </CardTitle>
              <CardDescription>Bulk import foods from a CSV file</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <Button variant="outline" onClick={downloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Template CSV
                </Button>
              </div>
              
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-lg font-medium">Drop CSV file here or click to browse</p>
                  <p className="text-sm text-muted-foreground">Maximum 5MB</p>
                </label>
                {csvFile && <p className="mt-2 text-primary">{csvFile.name}</p>}
              </div>
              
              {loading && (
                <div className="text-center py-4">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                  <p className="mt-2">Validating CSV...</p>
                </div>
              )}
              
              {csvValidation && (
                <Card className="bg-muted">
                  <CardContent className="pt-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="font-medium">{csvValidation.validRows} valid rows ready to import</span>
                    </div>
                    {csvValidation.warnings?.length > 0 && (
                      <div className="flex items-center gap-2 text-amber-600">
                        <AlertTriangle className="h-5 w-5" />
                        <span>{csvValidation.warnings.length} rows with warnings</span>
                      </div>
                    )}
                    {csvValidation.invalidRows > 0 && (
                      <div className="flex items-center gap-2 text-red-500">
                        <AlertTriangle className="h-5 w-5" />
                        <span>{csvValidation.invalidRows} rows with errors (will be skipped)</span>
                      </div>
                    )}
                    
                    {csvValidation.errors?.length > 0 && (
                      <details className="text-sm">
                        <summary className="cursor-pointer text-muted-foreground">View errors</summary>
                        <ul className="mt-2 space-y-1 pl-4">
                          {csvValidation.errors.slice(0, 10).map((err, i) => (
                            <li key={i} className="text-red-600">Row {err.row}: {err.error}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                    
                    <Button 
                      onClick={confirmCsvImport} 
                      disabled={csvImporting || csvValidation.validRows === 0}
                      className="w-full"
                    >
                      {csvImporting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        `Import ${csvValidation.validRows} Valid Items to Review Queue`
                      )}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Results Display */}
      {results && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Generated {results.generated} items
            </CardTitle>
            <CardDescription>Batch ID: {results.batchId}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {results.items?.map((item, idx) => (
                <div key={idx} className="p-3 bg-muted rounded-lg flex items-center justify-between">
                  <div>
                    <span className="font-medium">{item.name || item.title}</span>
                    {item.glycemicIndex !== undefined && (
                      <Badge variant="outline" className="ml-2">GI: {item.glycemicIndex}</Badge>
                    )}
                    {item.gi !== undefined && (
                      <Badge variant="outline" className="ml-2">GI: {item.gi}</Badge>
                    )}
                    {item.category && (
                      <Badge variant="secondary" className="ml-2">{item.category}</Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {item.duplicateWarnings?.length > 0 && (
                      <Badge variant="destructive" className="flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Duplicate
                      </Badge>
                    )}
                    {item.validationWarnings?.length > 0 && (
                      <Badge variant="outline" className="text-amber-600 border-amber-600">
                        {item.validationWarnings.length} warnings
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <Button 
              className="w-full mt-4" 
              variant="outline"
              onClick={() => window.location.href = '/admin/review-queue'}
            >
              Go to Review Queue
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
